import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X, Upload, FileSpreadsheet } from "lucide-react";
import { usePostApi } from "../../hooks/post_api_hook/usePostApi";
import { url } from "../../utils/confserveur";
import {
    lireClasseur, reconnaitreFichier, rangerFichiers, lignesDImport, etatDeLigne, demandeDeVerification,
    lireVerification, nomsAConfirmer, envoiImport, lireResultat, messageDErreur, classesHorsTanoEd,
} from "../../utils/importEvalNat";
import LignesImportEvalNat, { accorder } from "./ImportEvalNatChefComponents/LignesImportEvalNat";
import NomsAConfirmerEvalNat from "./ImportEvalNatChefComponents/NomsAConfirmerEvalNat";
import ResultatImportEvalNat from "./ImportEvalNatChefComponents/ResultatImportEvalNat";

/**
 * La fenêtre d'import des résultats nationaux (plan « Nouvelle fenêtre d'import » du 30/10/2026, étape 3), ouverte
 * par le chef ou le délégué depuis le tableau de bord. Trois temps, sans rien qui bloque :
 *   1. les fichiers, déposés d'un coup : la restitution de l'établissement et les fichiers de tests des classes ;
 *   2. la vérification, automatique : une ligne par classe et matière, et les noms à confirmer ;
 *   3. l'import, niveau par niveau, puis son résultat.
 * Les règles vivent dans utils/importEvalNat ; la fenêtre affiche, demande et envoie. Réimporter ne casse rien : le
 * serveur complète ce qui manque et répond « déjà importé » pour le reste.
 */

const NIVEAUX = ["6", "5", "4"];
const DELAI_VERIFICATION_MS = 400;
const RAISONS_MONTREES = 3;
const MATIERES = ["maths", "français"];

// Porte allégée (31/10/2026) : l'import des élèves s'ouvre dans un nouvel onglet, la fenêtre garde ses fichiers ; au
// retour, « Revérifier » (avant l'import) ou « Revenir aux fichiers » (après) relit les classes et relance la
// vérification, qui trouve les élèves ajoutés.
const ouvrirImportEleves = () => window.open("/home/import-etablissement", "_blank", "noopener");

/** Ce qu'on dit d'un fichier écarté : ses raisons, telles que les contrôles les donnent, sans leur point final. */
const raisonsDe = (fichier) => {
    const raisons = fichier.type === "inconnu" ? [fichier.raison]
        : fichier.type === "tests" ? fichier.erreurs
            : fichier.feuillesEnEchec.map(({ feuille, raison }) => `feuille « ${feuille} » : ${raison}`);
    return raisons.map((raison) => String(raison).replace(/[.\s]+$/, ""));
};

/**
 * Pourquoi une ligne ne part pas, pour le résultat ; null pour une classe déjà importée sans nouveau fichier, et pour
 * une classe absente de TanoEd, dite à part en une ligne (01/11/2026).
 */
const raisonDeNePasPartir = (ligne, verification, classesSansResultats, restitutionDeposee) => {
    const etat = etatDeLigne(ligne, verification);
    if (etat === "sans_tests") return classesSansResultats.has(`${ligne.classe.nom}|${ligne.matiere}`) ? "le fichier de tests manque" : null;
    if (etat === "sans_restitution") return restitutionDeposee ? "la classe est absente de la restitution" : "la restitution manque";
    if (etat === "classe_vide") return "la classe n'a encore aucun élève dans TanoEd";
    if (etat === "refusee") return verification.parLigne.get(ligne.cle).erreur;
    return null;
};

/**
 * `etablissementData` : l'établissement et ses classes, avec leur nombre d'élèves ; `onRelireClasses` les recharge
 * (refreshEtablissementData du contexte), pour qu'une classe remplie entre-temps ne soit plus vide.
 */
function ModalImportEvalNatChef({ isOpen, onClose, allMissingClasses = { total: 0, byNiveauMatiere: {} }, etablissementData, onRelireClasses }) {
    const champFichiers = useRef(null);
    const [fichiers, setFichiers] = useState([]);
    const [illisibles, setIllisibles] = useState([]);
    const [enLecture, setEnLecture] = useState(0);
    const [survol, setSurvol] = useState(false);
    const [verification, setVerification] = useState(null);
    const [verificationEnCours, setVerificationEnCours] = useState(false);
    const [erreurVerification, setErreurVerification] = useState(null);
    const [relance, setRelance] = useState(0);
    const [choix, setChoix] = useState({});
    const [envoi, setEnvoi] = useState(null);
    const [resultat, setResultat] = useState(null);
    const [importLance, setImportLance] = useState(false);
    const [, verifier] = usePostApi(url + "eleves/check-missing-for-eval-nat/");
    const [, importer] = usePostApi(url + "evaluations_nat/create_national_evaluation/");

    const classes = etablissementData?.classes;
    const etablissementId = etablissementData?.id;
    const range = useMemo(() => rangerFichiers(fichiers), [fichiers]);
    const lignes = useMemo(() => lignesDImport(range, classes), [range, classes]);
    const classesSansResultats = useMemo(() => new Set(Object.values(allMissingClasses?.byNiveauMatiere || {})
        .flatMap(({ matiere, classes: noms }) => noms.map((nom) => `${nom}|${matiere}`))), [allMissingClasses]);
    const pretes = useMemo(() => lignes.filter((l) => etatDeLigne(l, verification) === "prete" && l.eleves.length > 0), [lignes, verification]);
    // Les questions ne portent que sur les lignes qui partiront.
    const questions = useMemo(() => nomsAConfirmer(pretes, verification, choix), [pretes, verification, choix]);
    const attendent = useMemo(() => lignes.filter((l) => etatDeLigne(l, verification) === "sans_tests"
        && classesSansResultats.has(`${l.classe.nom}|${l.matiere}`)).length, [lignes, verification, classesSansResultats]);
    const restitutionDeposee = Boolean(range.restitution);

    // La vérification part d'elle-même à chaque changement de fichiers ; une réponse arrivée trop tard est ignorée.
    useEffect(() => {
        setVerification(null);
        setErreurVerification(null);
        const demande = demandeDeVerification(lignes, etablissementId);
        if (!demande) {
            setVerificationEnCours(false);
            return undefined;
        }
        let actuelle = true;
        setVerificationEnCours(true);
        const minuteur = setTimeout(async () => {
            const reponse = await verifier(demande);
            if (!actuelle) return;
            setVerificationEnCours(false);
            if (reponse.success) setVerification(lireVerification(reponse.data, lignes));
            else setErreurVerification(messageDErreur(reponse, "verification"));
        }, DELAI_VERIFICATION_MS);
        return () => {
            actuelle = false;
            clearTimeout(minuteur);
        };
    }, [lignes, etablissementId, relance]); // eslint-disable-line react-hooks/exhaustive-deps

    // Un fichier redéposé remplace sa version précédente ; les fichiers se lisent en même temps.
    const deposer = useCallback(async (liste) => {
        const nouveaux = Array.from(liste || []);
        if (nouveaux.length === 0) return;
        setResultat(null);
        setEnLecture((n) => n + nouveaux.length);
        const lus = await Promise.all(nouveaux.map((fichier) => lireClasseur(fichier)
            .then((classeur) => ({ nom: fichier.name, reconnu: reconnaitreFichier(classeur, classes) }))
            .catch((erreur) => ({ nom: fichier.name, raison: erreur.message }))));
        const noms = new Set(lus.map((l) => l.nom));
        setFichiers((avant) => [...avant.filter((f) => !noms.has(f.nom)), ...lus.filter((l) => l.reconnu).map((l) => l.reconnu)]);
        setIllisibles((avant) => [...avant.filter((f) => !noms.has(f.nom)), ...lus.filter((l) => !l.reconnu).map(({ nom, raison }) => ({ nom, raison }))]);
        setEnLecture((n) => n - nouveaux.length);
    }, [classes]);

    const choisir = useCallback((cle, valeur) => setChoix((avant) => ({ ...avant, [cle]: valeur })), []);
    const annuler = useCallback((cle) => setChoix((avant) => {
        const { [cle]: _retire, ...reste } = avant;
        return reste;
    }), []);
    // « Revérifier », sous les inconnus et les classes vides : les classes sont relues, la vérification repart, les
    // réponses déjà données restent.
    const reverifier = useCallback(() => {
        onRelireClasses?.();
        setRelance((n) => n + 1);
    }, [onRelireClasses]);

    // L'envoi part niveau par niveau (choix 2 validé) : un niveau qui échoue n'empêche pas les autres.
    const lancerImport = useCallback(async () => {
        const parNiveau = NIVEAUX.map((niveau) => ({ niveau, lignes: pretes.filter((l) => l.niveau === niveau) }))
            .filter((groupe) => groupe.lignes.length > 0);
        const lignesDuResultat = [];
        const erreurs = [];
        setImportLance(true);
        for (let rang = 0; rang < parNiveau.length; rang += 1) {
            const { niveau, lignes: duNiveau } = parNiveau[rang];
            setEnvoi({ niveau, rang: rang + 1, total: parNiveau.length });
            const corps = envoiImport(duNiveau, verification, choix, etablissementId);
            const reponse = await importer(corps);
            if (reponse.success) lignesDuResultat.push(...lireResultat(reponse.data, corps).lignes);
            else erreurs.push({ niveau, message: messageDErreur(reponse, "import") });
        }
        setEnvoi(null);
        setResultat({ lignes: lignesDuResultat, erreurs });
    }, [pretes, verification, choix, etablissementId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Après un import, la page se recharge pour montrer les nouveaux résultats, comme avant.
    const fermer = useCallback(() => {
        if (importLance) {
            window.location.reload();
            return;
        }
        setFichiers([]);
        setIllisibles([]);
        setChoix({});
        setResultat(null);
        onClose();
    }, [importLance, onClose]);

    if (!isOpen) return null;

    const ecartes = [
        ...illisibles.map(({ nom, raison }) => ({ nom, raisons: [String(raison).replace(/[.\s]+$/, "")] })),
        ...range.refuses.map((fichier) => ({ nom: fichier.nom, raisons: raisonsDe(fichier) })),
    ];
    // Une classe vide est dite une fois pour ses deux matières ; les classes absentes de TanoEd, en une ligne à part.
    const nonEnvoyees = lignes.reduce((liste, ligne) => {
        const raison = raisonDeNePasPartir(ligne, verification, classesSansResultats, restitutionDeposee);
        if (!raison) return liste;
        const nom = etatDeLigne(ligne, verification) === "classe_vide" ? ligne.classe.nom : `${ligne.classe.nom} ${ligne.matiere}`;
        return liste.some((e) => e.nom === nom) ? liste : [...liste, { nom, raison }];
    }, []);
    const horsTanoEd = classesHorsTanoEd(lignes);
    const sansResultats = MATIERES.map((matiere) => {
        const noms = Object.values(allMissingClasses?.byNiveauMatiere || {}).filter((g) => g.matiere === matiere).flatMap((g) => g.classes);
        return noms.length > 0 ? `${matiere} ${noms.join(", ")}` : null;
    }).filter(Boolean);
    const occupe = enLecture > 0 || verificationEnCours || Boolean(envoi);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={envoi ? undefined : fermer} aria-hidden="true" />
            <div role="dialog" aria-modal="true" aria-label="Importer les résultats des évaluations nationales" className="relative bg-white text-slate-800 rounded-3xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
                <div className="flex items-center gap-3 px-6 py-4 bg-blue-900 text-white">
                    <Upload className="w-5 h-5 flex-shrink-0" />
                    <h2 className="flex-1 min-w-0 text-lg font-bold">Importer les résultats des évaluations nationales</h2>
                    <button type="button" onClick={fermer} disabled={Boolean(envoi)} aria-label="Fermer" className="p-1 rounded-lg hover:bg-white/10 disabled:opacity-40">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {envoi ? (
                    <div role="status" className="absolute inset-0 top-[60px] z-10 bg-white/95 flex flex-col items-center justify-center gap-3">
                        <div className="w-10 h-10 border-4 border-blue-900 border-t-transparent rounded-full animate-spin" />
                        <p className="text-base font-semibold text-slate-800">Import de la {envoi.niveau}e… ({envoi.rang} sur {envoi.total})</p>
                    </div>
                ) : null}

                <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-6">
                    {resultat ? (
                        <ResultatImportEvalNat
                            resultat={resultat}
                            nonEnvoyees={nonEnvoyees}
                            horsTanoEd={horsTanoEd}
                            onOuvrirImportEleves={ouvrirImportEleves}
                            onRevenir={() => {
                                setResultat(null);
                                reverifier();
                            }}
                            onFermer={fermer}
                        />
                    ) : (
                        <>
                            <div className="flex flex-col gap-3">
                                <div className="flex items-baseline gap-2 flex-wrap">
                                    <span className="w-6 h-6 rounded-full bg-blue-900 text-white text-xs font-bold grid place-items-center">1</span>
                                    <h3 className="text-base font-bold text-slate-800">Vos fichiers</h3>
                                    <span className="text-sm text-slate-500">Déposez tout d'un coup : chaque fichier va dans sa case.</span>
                                </div>
                                {sansResultats.length > 0 ? (
                                    <p className="text-sm text-amber-700">Encore sans résultats : {sansResultats.join(" · ")}.</p>
                                ) : null}
                                <div
                                    onDragOver={(e) => { e.preventDefault(); setSurvol(true); }}
                                    onDragLeave={() => setSurvol(false)}
                                    onDrop={(e) => { e.preventDefault(); setSurvol(false); deposer(e.dataTransfer.files); }}
                                    className={`rounded-2xl border-2 border-dashed p-4 flex flex-col gap-3 transition-colors ${survol ? "border-blue-500 bg-blue-50" : "border-cyan-600/60 bg-slate-50"}`}
                                >
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div className="rounded-xl bg-white border border-slate-200 p-3 flex flex-col gap-1 min-w-0">
                                            <b className="text-sm text-slate-800">Restitution de l'établissement</b>
                                            <span className={`text-xs font-mono break-all ${range.restitution ? "text-emerald-700" : "text-slate-400"}`}>
                                                {range.restitution ? `✓ ${range.restitution.nom}` : "Pas encore déposée"}
                                            </span>
                                        </div>
                                        <div className="rounded-xl bg-white border border-slate-200 p-3 flex flex-col gap-1 min-w-0">
                                            <b className="text-sm text-slate-800">Tests spécifiques, un fichier par classe et matière</b>
                                            <span className={`text-xs font-mono break-all ${range.tests.length ? "text-emerald-700" : "text-slate-400"}`}>
                                                {range.tests.length ? `✓ ${accorder(range.tests.length, "fichier", "fichiers")}` : "Aucun fichier"}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-3">
                                        <button type="button" onClick={() => champFichiers.current?.click()} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold bg-blue-900 text-white hover:bg-blue-800">
                                            <FileSpreadsheet className="w-4 h-4" />
                                            Choisir des fichiers
                                        </button>
                                        <span className="text-sm text-slate-500">
                                            {enLecture > 0 ? `Lecture de ${accorder(enLecture, "fichier", "fichiers")}…` : "ou glissez-les ici."}
                                        </span>
                                        <input
                                            ref={champFichiers}
                                            type="file"
                                            multiple
                                            accept=".xlsx,.xls,.ods"
                                            aria-label="Fichiers des évaluations nationales"
                                            className="hidden"
                                            onChange={(e) => { deposer(e.target.files); e.target.value = ""; }}
                                        />
                                    </div>
                                </div>
                                {range.remplaces.map(({ ancien, nouveau }) => (
                                    <p key={`${ancien}|${nouveau}`} className="text-sm text-slate-500">« {nouveau} » remplace « {ancien} » : même classe, même matière.</p>
                                ))}
                                {(range.restitution?.feuillesEnEchec || []).map(({ feuille, raison }) => (
                                    <p key={feuille} className="text-sm text-amber-700">Restitution, feuille « {feuille} » écartée : {raison}.</p>
                                ))}
                                {ecartes.length > 0 ? (
                                    <div className="rounded-xl border border-red-200 bg-red-50 p-3 flex flex-col gap-1.5 text-sm text-red-800">
                                        <b>{accorder(ecartes.length, "fichier écarté", "fichiers écartés")}</b>
                                        {ecartes.map(({ nom, raisons }) => (
                                            <p key={nom}>
                                                <span className="font-mono text-xs">{nom}</span> : {raisons.slice(0, RAISONS_MONTREES).join(" ; ")}
                                                {raisons.length > RAISONS_MONTREES ? ` ; et ${accorder(raisons.length - RAISONS_MONTREES, "autre raison", "autres raisons")}` : ""}.
                                            </p>
                                        ))}
                                    </div>
                                ) : null}
                            </div>

                            {lignes.length > 0 ? (
                                <div className="flex flex-col gap-3">
                                    <div className="flex items-baseline gap-2 flex-wrap">
                                        <span className="w-6 h-6 rounded-full bg-blue-900 text-white text-xs font-bold grid place-items-center">2</span>
                                        <h3 className="text-base font-bold text-slate-800">Vérification</h3>
                                        <span className="text-sm text-slate-500">Automatique, dès que les fichiers sont là.</span>
                                    </div>
                                    {erreurVerification ? (
                                        <div role="alert" className="flex flex-wrap items-center gap-3 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm font-semibold text-red-700">
                                            {erreurVerification}
                                            <button type="button" onClick={() => setRelance((n) => n + 1)} className="px-3 py-1 rounded-lg bg-white border border-red-300 text-red-700 hover:bg-red-100">
                                                Réessayer
                                            </button>
                                        </div>
                                    ) : null}
                                    <LignesImportEvalNat
                                        lignes={lignes}
                                        verification={verification}
                                        verificationEnCours={verificationEnCours}
                                        questions={questions}
                                        choix={choix}
                                        restitutionDeposee={restitutionDeposee}
                                        classesSansResultats={classesSansResultats}
                                        onOuvrirImportEleves={ouvrirImportEleves}
                                        onReverifier={reverifier}
                                    />
                                    <NomsAConfirmerEvalNat
                                        questions={questions}
                                        choix={choix}
                                        onChoisir={choisir}
                                        onAnnuler={annuler}
                                        onOuvrirImportEleves={ouvrirImportEleves}
                                        onReverifier={reverifier}
                                    />
                                </div>
                            ) : null}

                            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4">
                                <span className="text-sm text-slate-500">
                                    {[
                                        accorder(pretes.length, "classe prête", "classes prêtes"),
                                        attendent ? `${attendent} ${attendent > 1 ? "attendent leur" : "attend son"} fichier de tests` : null,
                                    ].filter(Boolean).join(" · ")}
                                </span>
                                <button
                                    type="button"
                                    onClick={lancerImport}
                                    disabled={pretes.length === 0 || occupe}
                                    className="px-5 py-2.5 rounded-xl text-sm font-bold bg-blue-900 text-white hover:bg-blue-800 disabled:bg-slate-300 disabled:cursor-not-allowed"
                                >
                                    Importer {accorder(pretes.length, "classe", "classes")}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ModalImportEvalNatChef;
